<template>
    <portlet 
        :icon="'fad fa-ruler'"
        :title="'Metrics'"
        :actionsBlock="true"
    >
        <template #portletBody>
            <menu-section
                :menu="menu"
                :entry="selectedEntry"
                @entryClick="menuClick($event)"
                class="p-1 mb-4">
            </menu-section>
  
            <div class="form-row align-items-end mb-3 justify-content-end">
                <div class="col-auto">
                    <label class="small mb-1" for="date-from">From</label>
                    <datepicker
                    id="dateFrom"
                    v-model="dateFrom"
                    :language="nl"
                    :full-month-name="true"
                    format="dd-MM-yyyy"
                    input-class="form-control"
                    :calendar-button="true"
                    calendar-button-icon="far fa-calendar-alt"
                    :clear-button="true"
                    clear-button-icon="far fa-times">
                    </datepicker>
                </div>
                <div class="col-auto">
                    <label class="small mb-1" for="date-to">To</label>
                    <datepicker
                    id="dateTo"
                    v-model="dateTo"
                    :language="nl"
                    :full-month-name="true"
                    format="dd-MM-yyyy"
                    input-class="form-control"
                    :calendar-button="true"
                    calendar-button-icon="far fa-calendar-alt"
                    :clear-button="true"
                    clear-button-icon="far fa-times"
                    :disabled-dates="disabledEndDates">
                    </datepicker>
                </div>
                <div class="col-auto">
                    <button class="btn filter-btn ml-2" @click="fetchData">Filter</button>
                </div>
            </div>
            <datatables
            v-if="renderedComponent && Can(rights.METRICS_VIEW)"
            :key="selectedEntry.id"
            tableID="metrics"
            :rows="selectedEntry.id === 1 ? peopleMetrics : categoryMetrics"
            :columns="selectedEntry.id === 1 ? getPeopleColumns() : getChatCategoryColumns()"
            :tableInitOptions="tableInitOptions"
            :tableClass="tableClass"
            routerLinkClass="dt-router-link"
            deleteClass="delete">
            </datatables>
      </template>
    </portlet>
</template>

<script>
    import Vue from 'vue';
    import store from '../../store.js';
    import { Can } from '../../../permission/ability.js';
    import { RIGHTS } from '../roles/rights';
    import {dataTables} from '../../components/mixins/dataTables.js';
    import menuSection from '../../components/notifications/menuSection.vue';
    import {nl} from "vuejs-datepicker/dist/locale";

    export default {
        components: { menuSection },
        mixins: [dataTables],
        data() {
            return {
                selectedEntry: {id:1, name: 'People'},
                menu: [{name: 'People', id: 1}, {name: 'Chat Category', id: 2}],
                peopleMetrics: [],
                categoryMetrics: [],
                dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                dateTo: new Date(),
                nl: nl
            }
        },
        async created() {
            this.rights = RIGHTS;
            await this.fetchData();
        },
        computed: {
            disabledEndDates() {
                return {
                    to: new Date(this.dateFrom),
                }
            },
        },  
        methods: {
            Can,
            menuClick(event) {
                this.selectedEntry = event
            },
            async fetchData() {
                this.$store.commit('setLoading',true);
                const metricsResponse = await store.dispatch('metrics/getAllMetrics', {
                    ApiClient:   Vue.prototype.$ApiClient,
                    departments: this.$store.getters.getUserData.departments,
                    dateFrom: this.dateFrom.toISOString().split('T')[0],
                    dateTo: this.dateTo.toISOString().split('T')[0]
                });

                if(metricsResponse.status === 200) {
                    this.processMetrics(metricsResponse.data);
                }
                this.$store.commit('setLoading',false)
            },
            processMetrics ({ chat = [], people = [] }) {
                const catMap = Object.create(null);

                chat.forEach(({ category, status, count }) => {
                    if (!catMap[category]) {
                        catMap[category] = {
                            chat_category: this.mapCategory(category),
                            open_chats: 0,
                            archived_chats: 0
                        };
                    }
                    const row = catMap[category];
                    if (status === 'open') {
                        row.open_chats += count;
                    } else {
                        row.archived_chats += count;
                    }
                });

                this.categoryMetrics = Object.values(catMap);

                this.peopleMetrics = people.map(p => {
                    const maxCat = p.category_metrics.reduce(
                        (best, cur) => cur.count > best.count ? cur : best,
                        { count: -1, category: '' }
                    ).category;

                    return {
                        name                  : p.name,
                        average_response_time : Number(p.response).toFixed(2),
                        accepted_chats        : p['accepted-transfer'] ? p['accepted-transfer'] : 0,
                        max_chat_category     : maxCat,
                        chats_taken_over      : p['given-transfer'] ?  p['given-transfer'] : 0
                    };
                });
            },
            mapCategory(category) {
                switch(category) {
                    case 'hours':
                        return "Hours";
                    case 'leave_requests':
                        return "Leave Requests";
                    case 'sickness':
                        return "Sickness";
                    case 'documents':
                        return "Documents";
                    case 'other':
                        return "Other";
                    default:
                        return "Unknown Category";
                }
            },
            getChatCategoryColumns() {
                return [
                    {data: 'chat_category', name: 'Chat Category', visible: true, searchable: true},
                    {data: 'open_chats', name: 'Open Chats', visible: true, searchable: true},
                    {data: 'archived_chats', name: 'Archived Chats', visible: true, searchable: true},
                ]
            },
            getPeopleColumns() {
                return [
                    {data: 'name', name: 'Name', visible: true, searchable: true},
                    {data: 'average_response_time', name: 'Avg. Response Time (s.)', visible: true, searchable: true},
                    {data: 'accepted_chats', name: 'Number of Accepted Chats', visible: true, searchable: true},
                    {data: 'max_chat_category', name: 'Max. Chat Category', visible: true, searchable: true},
                    {data: 'chats_taken_over', name: 'Chats Taken Over', visible: true, searchable: true},
                ]
            }
        }
    }
</script>

<style lang="scss" scoped>
    .btn.filter-btn {
        padding: 1rem 2rem;
        font-size: 1.6rem;
        border-radius: 10px;
        line-height: 1;
        color: #fff;
        font-weight: 600;
        background-color: #146F70;
        border: none;
    }
</style>